<template>
  <div v-title="$route.meta.title">
    <div class="page-heading">
      <el-breadcrumb separator-class="el-icon-arrow-right">
        <el-breadcrumb-item :to="{ path: '/' }">首页</el-breadcrumb-item>
        <el-breadcrumb-item>考试培训管理</el-breadcrumb-item>
        <el-breadcrumb-item :to="{ path: '/training/intern-course' }">实习生教程管理</el-breadcrumb-item>
        <el-breadcrumb-item>{{ $route.meta.title }}</el-breadcrumb-item>
      </el-breadcrumb>
    </div>

    <el-form
      ref="form"
      :model="form"
      :rules="formRules"
      size="medium"
      label-width="140px"
      label-position="left"
      @submit.native.prevent="onSubmit()">
      <layout-panel>
        <template #header>表单</template>
        <el-alert
          v-if="isRelationEditLocked"
          class="content-lock-alert"
          type="info"
          :closable="false"
          show-icon
          title="当前教程已上架或已下架，仅可修改标题、上传内容" />
        <div class="medium">
          <el-form-item label="标题" prop="title" class="form-item-full">
            <el-input
              v-model="form.title"
              clearable
              maxlength="100"
              show-word-limit
              placeholder="请输入教程标题" />
          </el-form-item>

          <el-form-item label="上传内容" prop="course_json">
            <div class="upload-panel">
              <el-upload
                ref="contentUpload"
                class="upload-panel__dropzone"
                drag
                action=""
                :auto-upload="false"
                :show-file-list="false"
                :multiple="true"
                :accept="uploadAcceptText"
                :disabled="uploading"
                :on-change="onCourseFileChange">
                <i class="el-icon-upload upload-panel__icon"></i>
                <div class="el-upload__text upload-panel__text">
                  将文件拖到此处，或<em>点击上传</em>
                </div>
                <div slot="tip" class="el-upload__tip upload-panel__tip">
                  {{ uploadConfig.tip }}
                </div>
              </el-upload>

              <div v-if="form.course_json.length" class="upload-list">
                <div
                  v-for="(file, index) in form.course_json"
                  :key="file.uid || file.url || index"
                  class="course-file-item">
                  <div v-if="isImageFile(file)" class="image-item">
                    <el-image
                      :src="file.url"
                      :preview-src-list="imagePreviewList"
                      fit="cover"
                      class="image-item__thumb" />
                    <div class="image-item__name" :title="file.name">{{ file.name }}</div>
                    <div class="image-item__actions">
                      <el-button type="text" @click="openFile(file)">查看</el-button>
                      <el-button type="text" @click="removeCourseFile(index)">删除</el-button>
                    </div>
                  </div>

                  <div v-else class="file-row">
                    <div class="file-row__meta">
                      <div class="file-row__icon">
                        <i :class="getFileIconClass(file)"></i>
                      </div>
                      <div class="file-row__text">
                        <div class="file-row__name" :title="file.name">{{ file.name }}</div>
                        <div class="file-row__desc">
                          {{ getFileTypeLabel(file) }} / {{ formatFileSize(file.size) }}
                          <span v-if="file.submit_admin_name"> / {{ file.submit_admin_name }}</span>
                          <span v-if="file.submit_at"> / {{ file.submit_at }}</span>
                        </div>
                      </div>
                    </div>
                    <div class="file-row__actions">
                      <el-button type="text" @click="openFile(file)">查看</el-button>
                      <el-button type="text" @click="removeCourseFile(index)">删除</el-button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </el-form-item>

          <el-form-item label="是否通用" prop="is_common">
            <el-radio-group
              v-model="form.is_common"
              :disabled="isRelationEditLocked"
              @change="onIsCommonChanged">
              <el-radio
                v-for="item in opts.isCommonMaps"
                :key="item.value"
                :label="item.value">
                {{ item.label }}
              </el-radio>
            </el-radio-group>
          </el-form-item>

          <el-form-item
            v-if="Number(form.is_common) === consts.IS_COMMON_DEDICATED"
            label="所属客户"
            prop="customerid">
            <remote-select
              v-model="form.customerid"
              class="width-max"
              clearable
              placeholder="请输入客户名称/编号搜索"
              :disabled="isRelationEditLocked"
              :query-request="getCustomerTable"
              :find-request="getCustomerDetail"
              @change="onCustomerChanged" />
          </el-form-item>

          <el-form-item label="所属知识库" prop="training_knowledge_id">
            <remote-select
              v-model="form.training_knowledge_id"
              class="width-max"
              clearable
              keyword-key="name"
              label-key="name"
              value-key="id"
              placeholder="请输入知识库名称搜索"
              :disabled="knowledgeDisabled"
              :query-request="queryTrainingKnowledge"
              :find-request="getTrainingKnowledge"
              :query-params="knowledgeQueryParams" />
          </el-form-item>

          <div class="form-actions">
            <el-button @click="$router.back()">取消</el-button>
            <el-button
              type="primary"
              native-type="submit"
              :loading="submitting || uploading">
              保存教程
            </el-button>
          </div>
        </div>
      </layout-panel>
    </el-form>
  </div>
</template>

<script>
import trainingMixins from '@views/training/training.mixins'
import {
  buildCourseKnowledgeQueryParams,
  COURSE_FILE_TYPES,
  consts,
  getCourseFileTypeByFile,
  getCourseUploadConfig,
  getFileExtension,
  isCourseRelationEditLocked,
  opts,
  validateCourseUploadFile,
} from '@views/training/training-course-intern.constants'
import moment from 'moment'

function createCourseJsonValidator () {
  return (rule, value, callback) => {
    if (!Array.isArray(value) || value.length < 1) {
      callback(new Error('请上传教程内容'))
      return
    }

    const fileCounts = value.reduce((counts, file) => {
      const fileType = getCourseFileTypeByFile(file)
      if (fileType) {
        counts[fileType] = (counts[fileType] || 0) + 1
      }
      return counts
    }, {})
    // 判断数量
    const exceededType = Object.keys(fileCounts).find(fileType => {
      const config = getCourseUploadConfig(fileType)
      return config.maxFiles > 0 && fileCounts[fileType] > config.maxFiles
    })
    if (exceededType) {
      callback(new Error(getCourseUploadConfig(exceededType).tip))
      return
    }
    callback()
  }
}

function createCustomerValidator (vm) {
  return (rule, value, callback) => {
    if (Number(vm.form.is_common) === consts.IS_COMMON_DEDICATED && !value) {
      callback(new Error('请选择所属客户'))
      return
    }
    callback()
  }
}

export default {
  mixins: [trainingMixins],
  props: {
    signedUrlApiName: {
      type: String,
      default: 'getTrainingSignedOssUrl',
    },
  },
  data () {
    return {
      id: 0,
      submitting: false,
      uploading: false,
      consts,
      opts,
      form: {
        title: '',
        is_common: consts.IS_COMMON_DEDICATED,
        customerid: null,
        training_knowledge_id: null,
        course_json: [],
        status: consts.STATUS_NEW,
      },
    }
  },
  computed: {
    ...Vuex.mapState({
      loginUser: state => state.login.user || {},
    }),
    formRules () {
      return {
        title: [
          { required: true, message: '请输入标题', trigger: 'blur' },
          { max: 100, message: '标题最多 100 个字符', trigger: 'blur' },
        ],
        target_usertype: [
          { required: true, message: '请选择用户类型', trigger: 'change' },
        ],
        is_common: [
          { required: true, message: '请选择是否通用', trigger: 'change' },
        ],
        customerid: [
          { validator: createCustomerValidator(this), trigger: 'change' },
        ],
        training_knowledge_id: [
          { required: true, message: '请选择所属知识库', trigger: 'change' },
        ],
        course_json: [
          { validator: createCourseJsonValidator(), trigger: 'change' },
        ],
      }
    },
    uploadConfig () {
      return getCourseUploadConfig()
    },
    uploadAcceptText () {
      return this.uploadConfig.accept.concat(this.uploadConfig.extensions.map(ext => `.${ext}`)).join(',')
    },
    isRelationEditLocked () {
      return isCourseRelationEditLocked(this.id, this.form.status)
    },
    knowledgeQueryParams () {
      return buildCourseKnowledgeQueryParams({
        is_common: this.form.is_common,
        customerid: this.form.customerid,
      })
    },
    knowledgeDisabled () {
      return this.isRelationEditLocked ||
        (Number(this.form.is_common) === consts.IS_COMMON_DEDICATED && !this.form.customerid)
    },
    imagePreviewList () {
      return this.form.course_json.filter(file => this.isImageFile(file)).map(file => file.url).filter(Boolean)
    },
  },
  created () {
    this.onLoad()
  },
  methods: {
    ...request.map([
      'getCustomerTable',
      'getCustomerDetail',
      'queryTrainingKnowledge',
      'getTrainingKnowledge',
      'getInternTrainingCourse',
      'createInternTrainingCourse',
      'updateInternTrainingCourse',
      'getTrainingSignedOssUrl',
    ]),
    onLoad () {
      if (this.$route.params.id) {
        this.id = Number(this.$route.params.id)
        this.loadDetail(this.id)
      }
    },
    loadDetail (id) {
      request.page('getInternTrainingCourse', { id }).then(({ data }) => {
        this.form.title = data.title || ''
        this.form.is_common = Number(data.is_common)
        this.form.id = Number(data.id)
        // 兼容客户字段回显问题
        this.form.customerid = Number(data.customerid) > 0 ? String(data.customerid) : null
        this.form.training_knowledge_id = Number(data.training_knowledge_id) || null
        this.form.course_json = Array.isArray(data.course_json)
          ? data.course_json.map(file => this.normalizeCourseFile(file))
          : []
        this.form.status = Number(data.status) || consts.STATUS_NEW
      })
    },

    onIsCommonChanged (value) {
      // 编辑上架或下架教程
      if (this.isRelationEditLocked) {
        return
      }
      if (Number(value) === consts.IS_COMMON_SHARED) {
        this.form.customerid = null
      }
      this.form.training_knowledge_id = null
      // 切换的时候清一下表单的验证
      this.clearCustomerValidate()
    },
    onCustomerChanged () {
      if (this.isRelationEditLocked) {
        return
      }
      this.form.training_knowledge_id = null
    },
    clearUploadCache () {
      if (this.$refs.contentUpload) {
        this.$refs.contentUpload.clearFiles()
      }
    },
    clearCustomerValidate () {
      this.$nextTick(() => {
        if (this.$refs.form) {
          this.$refs.form.clearValidate(['customerid', 'training_knowledge_id'])
        }
      })
    },
    async onCourseFileChange (file) {
      const raw = file.raw
      if (!raw) {
        return
      }

      const validation = validateCourseUploadFile(raw, this.form.course_json)
      if (!validation.valid) {
        this.$message.error(validation.message)
        this.clearUploadCache()
        return
      }

      this.uploading = true
      const loading = this.$loading({
        lock: true,
      })
      try {
        const uploadedFile = await this.uploadCourseFile(raw, this.signedUrlApiName)
        if (validation.replaceAll) {
          this.form.course_json = [uploadedFile]
        } else {
          this.form.course_json.push(uploadedFile)
        }
        this.$message.success(validation.replaceAll ? '文件已替换' : '上传成功')
        this.$refs.form && this.$refs.form.validateField('course_json')
      } catch (error) {
        this.$message.error(error.message || '上传失败')
      } finally {
        this.uploading = false
        loading.close()
        this.clearUploadCache()
      }
    },
    async uploadCourseFile (file, apiName) {
      const ext = getFileExtension(file.name)
      const { data } = await request.common(apiName, {
        file_name: file.name,
        ext,
        content_type: file.type || 'application/octet-stream',
      })
      const uploadRes = await fetch(data.signedUrl, {
        method: 'PUT',
        headers: new Headers({
          'Content-Type': data.content_type,
        }),
        body: file,
      })
      if (!uploadRes.ok) {
        throw new Error(`上传失败: ${uploadRes.status}`)
      }

      return this.formatStandardFile({
        name: file.name,
        url: data.url,
        uid: Date.now() + Math.random(),
        size: file.size,
        file_type: getCourseFileTypeByFile(file),
        mime_type: file.type || 'application/octet-stream',
      }, this.loginUser)
    },

    // 附件上传,标准化字段结构
    formatStandardFile (file, loginUser) {
      const fileType = file.file_type || getCourseFileTypeByFile(file)
      return {
        submit_admin_id: loginUser.id,
        submit_admin_name: loginUser.name,
        submit_at: moment().format('YYYY-MM-DD HH:mm:ss'),
        name: file.name,
        size: file.size,
        url: file.url,
        uid: file.uid,
        type: fileType,
        mime_type: file.mime_type || '',
        extension: getFileExtension(file.name),
      }
    },
    normalizeCourseFile (file) {
      const fileType = getCourseFileTypeByFile(file)
      return {
        ...file,
        type: fileType || file.type || '',
        mime_type: file.mime_type || (
          typeof file.type === 'string' && file.type.indexOf('/') !== -1
            ? file.type
            : ''
        ),
        extension: file.extension || getFileExtension(file.name),
      }
    },
    isImageFile (file) {
      return getCourseFileTypeByFile(file) === COURSE_FILE_TYPES.IMAGE
    },
    getFileTypeLabel (file) {
      const labels = {
        [COURSE_FILE_TYPES.IMAGE]: '图片',
        [COURSE_FILE_TYPES.VIDEO]: '视频',
        [COURSE_FILE_TYPES.PDF]: 'PDF',
        [COURSE_FILE_TYPES.PPT]: 'PPT',
        [COURSE_FILE_TYPES.WORD]: 'Word',
        [COURSE_FILE_TYPES.EXCEL]: 'Excel',
      }
      return labels[getCourseFileTypeByFile(file)] || '文件'
    },
    getFileIconClass (file) {
      return getCourseFileTypeByFile(file) === COURSE_FILE_TYPES.VIDEO
        ? 'el-icon-video-camera'
        : 'el-icon-document'
    },
    removeCourseFile (index) {
      this.form.course_json.splice(index, 1)
      this.$refs.form && this.$refs.form.validateField('course_json')
    },
    openFile (file) {
      if (!file || !file.url) {
        return
      }
      window.open(file.url, '_blank')
    },
    formatFileSize (size) {
      const value = Number(size) || 0
      if (value >= 1024 * 1024) {
        return `${(value / 1024 / 1024).toFixed(2)}MB`
      }
      if (value >= 1024) {
        return `${(value / 1024).toFixed(2)}KB`
      }
      return `${value}B`
    },
    getSubmitPayload () {
      const payload = filter.copyJson(this.form)
      if (Number(payload.is_common) === consts.IS_COMMON_SHARED) {
        payload.customerid = 0
      }
      delete payload.status
      return payload
    },
    onSubmit () {
      this.$refs.form.validate(valid => {
        if (!valid) {
          this.$message.error('请检查表单填写是否正确')
          return
        }
        this.submitting = true
        const requestName = this.id ? 'updateInternTrainingCourse' : 'createInternTrainingCourse'
        const params = this.id
          ? { id: this.id, ...this.getSubmitPayload() }
          : this.getSubmitPayload()
        request.common(requestName, params).then(() => {
          this.$message.success(this.id ? '保存成功' : '创建成功')
          this.$router.push({ path: '/training/intern-course' })
        }).finally(() => {
          this.submitting = false
        })
      })
    },
  },
}
</script>

<style scoped lang="scss">
.form-item-full {
  width: 100%;
}

.upload-panel {
  width: 100%;
}

.upload-panel__dropzone {
  width: 100%;
  max-width: 760px;
}

.upload-panel__dropzone >>> .el-upload,
.upload-panel__dropzone >>> .el-upload-dragger {
  width: 100%;
}

.upload-panel__dropzone >>> .el-upload-dragger {
  height: auto;
  min-height: 160px;
  padding: 48px 32px;
  border: 1px dashed #8cc5ff;
  border-radius: 6px;
  background: #f8fbff;
}

.upload-panel__dropzone >>> .el-upload-dragger:hover {
  border-color: #409EFF;
}

.upload-panel__icon {
  font-size: 44px;
  color: #409EFF;
  margin-bottom: 12px;
}

.upload-panel__text {
  font-size: 14px;
  color: #606266;
  line-height: 22px;
}

.upload-panel__text em {
  font-style: normal;
  color: #409EFF;
}

.upload-panel__tip {
  margin-top: 12px;
  line-height: 20px;
  color: #909399;
}

.upload-list {
  margin-top: 16px;
  max-width: 960px;
}

.course-file-item + .course-file-item {
  margin-top: 8px;
}

.image-list {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 16px;
}

.image-item {
  max-width: 300px;
  border: 1px solid #d9ecff;
  border-radius: 6px;
  padding: 10px;
  background: #f8fbff;
}

.image-item__thumb {
  width: 100%;
  height: 100px;
  border-radius: 4px;
  background: #f5f7fa;
}

.image-item__name {
  margin-top: 8px;
  font-size: 12px;
  line-height: 18px;
  color: #303133;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.image-item__actions {
  display: flex;
  justify-content: space-between;
  margin-top: 4px;
}

.file-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px;
  border: 1px solid #d9ecff;
  border-radius: 6px;
  background: #f8fbff;
}

.file-row + .file-row {
  margin-top: 8px;
}

.file-row__meta {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}

.file-row__icon {
  width: 36px;
  height: 36px;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 18px;
  background: #ecf5ff;
}

.file-row__icon i {
  font-size: 24px;
  color: #409EFF;
}

.file-row__text {
  min-width: 0;
}

.file-row__name {
  font-size: 13px;
  color: #303133;
  line-height: 20px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.file-row__desc {
  margin-top: 2px;
  font-size: 12px;
  line-height: 18px;
  color: #909399;
}

.file-row__actions {
  flex-shrink: 0;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 28px;
}

.form-actions .el-button + .el-button {
  margin-left: 12px;
}

.content-lock-alert {
  margin-bottom: 16px;
}
</style>



// WEBPACK FOOTER //
// src/views/training/pages/course/InternTrainingCourseForm.vue